import { ArrowBottomRightIcon, ArrowRightIcon, ArrowTopRightIcon } from '@radix-ui/react-icons';
import { Badge, Skeleton, Slider } from '@radix-ui/themes';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { getNextRankChange, rankSwitchLabel } from 'helpers/ranks';
import { getNextRank } from 'helpers/utils';
import type { PlayerInfoType } from 'types/player';

export const RankBox: React.FC<{ playerInfo?: PlayerInfoType; loading: boolean }> = ({ playerInfo, loading }) => {
  const navigate = useNavigate();
  const rankVariationClassName = classNames(
    'flex',
    'items-center',
    'justify-center',
    'py-1',
    'px-2',
    'gap-1.5',
    'rounded-md',
    'text-white',
    'text-xs',
    'tracking-wide',
    'font-medium',
    'whitespace-normal shrink',
    { 'bg-accent-accent-9': playerInfo?.first_rank_difference && playerInfo.first_rank_difference > 0 },
    { 'bg-neutral-neutral-7': playerInfo && playerInfo.first_rank_difference === 0 },
    { 'bg-semantic-error-9': playerInfo?.first_rank_difference && playerInfo.first_rank_difference < 0 },
  );

  const arrow =
    playerInfo && playerInfo.first_rank_difference === 0 ? (
      <ArrowRightIcon width={16} height={16} viewBox='0 0 16 16' style={{ fill: 'currentColor' }} />
    ) : playerInfo?.first_rank_difference && playerInfo.first_rank_difference > 0 ? (
      <ArrowTopRightIcon width={16} height={16} viewBox='0 0 16 16' style={{ fill: 'currentColor' }} />
    ) : (
      <ArrowBottomRightIcon width={16} height={16} viewBox='0 0 16 16' style={{ fill: 'currentColor' }} />
    );

  const changeRankText =
    playerInfo && playerInfo.first_rank_difference === 0
      ? 'invariato '
      : playerInfo?.first_rank_difference && playerInfo.first_rank_difference > 0
        ? `+${playerInfo.first_rank_difference} `
        : `${playerInfo?.first_rank_difference} `;

  const percentage =
    playerInfo && playerInfo.points_to_rank_switch
      ? Math.round(Math.abs(((playerInfo.fit_points ?? 0) / playerInfo.points_to_rank_switch) * 100))
      : 0;

  const rankBoxDialog = (
    <div
      className='text-accent-accent-12 hover:bg-limea-2 text-sm leading-tight font-medium underline underline-offset-4 hover:cursor-pointer'
      onClick={() => {
        navigate(`${location.pathname}/partite#spiega-classifica`);
      }}
    >
      Spiega classifica
    </div>
  );

  const nextChangeCopy = () => {
    if (!playerInfo) return null;
    const nextRankChange = getNextRankChange();
    if (playerInfo.first_rank_difference && playerInfo.first_rank_difference >= 0) {
      //no change or increase in rank
      return (
        <div
          id='copy'
          className='text-neutral-neutral-12 flex-1 items-center justify-between self-stretch text-sm font-normal'
        >
          È la classifica simulata che il giocatore dovrebbe ottenere al prossimo passaggio:
          <span className='ml-1 text-lg font-bold'>{nextRankChange}</span>. È calcolata sulle partite e i bonus attuali
          del giocatore.
        </div>
      );
    } else {
      //rank decrease
      return (
        <div
          id='copy'
          className='text-neutral-neutral-12 flex-1 items-center justify-between self-stretch text-sm font-normal'
        >
          È la classifica simulata basata sui punti e bonus attuali.{' '}
          {nextRankChange.includes('fine anno') ? (
            <>
              La retrocessione avverrà, nel caso, al prossimo aggiornamento di
              <span className='ml-1 text-lg font-bold'>{nextRankChange}</span>
            </>
          ) : (
            <>
              Il
              <span className='ml-1 text-lg font-bold'>{nextRankChange}</span> ci sarà un aggiornamento ma la classifica
              rimarrà invariata. L'eventuale retrocessione avverrà, nel caso, a fine anno FITP.
            </>
          )}
        </div>
      );
    }
  };

  return (
    <div id='container' className='bg-accent-accent-4 flex flex-col items-start gap-4 self-stretch rounded-xl p-3'>
      <div id='topbar' className='flex flex-col items-start gap-2 self-stretch'>
        <div className='flex items-center justify-between gap-2 self-stretch'>
          <div id='topbarsx' className='flex items-center gap-2'>
            <div className='h-4 w-4 fill-transparent'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='align-top'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
              >
                <rect width='16' height='16' fill='white' fillOpacity='0.01' />
                <path
                  fillRule='evenodd'
                  clipRule='evenodd'
                  d='M12.2667 1.06668C12.5612 1.06668 12.8 1.30546 12.8 1.60001V14.4C12.8 14.6945 12.5612 14.9333 12.2667 14.9333C11.9722 14.9333 11.7333 14.6945 11.7333 14.4V1.60001C11.7333 1.30546 11.9722 1.06668 12.2667 1.06668ZM10.1333 3.20001C10.4279 3.20001 10.6667 3.4388 10.6667 3.73335V14.4C10.6667 14.6945 10.4279 14.9333 10.1333 14.9333C9.83878 14.9333 9.6 14.6945 9.6 14.4V3.73335C9.6 3.4388 9.83878 3.20001 10.1333 3.20001ZM14.4 3.20001C14.6945 3.20001 14.9333 3.4388 14.9333 3.73335V14.4C14.9333 14.6945 14.6945 14.9333 14.4 14.9333C14.1055 14.9333 13.8667 14.6945 13.8667 14.4V3.73335C13.8667 3.4388 14.1055 3.20001 14.4 3.20001ZM5.86667 4.26668C6.16122 4.26668 6.4 4.50547 6.4 4.80001V14.4C6.4 14.6945 6.16122 14.9333 5.86667 14.9333C5.57212 14.9333 5.33333 14.6945 5.33333 14.4V4.80001C5.33333 4.50547 5.57212 4.26668 5.86667 4.26668ZM1.6 5.33335C1.89455 5.33335 2.13333 5.57213 2.13333 5.86668V14.4C2.13333 14.6945 1.89455 14.9333 1.6 14.9333C1.30545 14.9333 1.06667 14.6945 1.06667 14.4V5.86668C1.06667 5.57213 1.30545 5.33335 1.6 5.33335ZM8 5.33335C8.29455 5.33335 8.53333 5.57213 8.53333 5.86668V14.4C8.53333 14.6945 8.29455 14.9333 8 14.9333C7.70545 14.9333 7.46667 14.6945 7.46667 14.4V5.86668C7.46667 5.57213 7.70545 5.33335 8 5.33335ZM3.73333 7.46668C4.02788 7.46668 4.26667 7.70547 4.26667 8.00001V14.4C4.26667 14.6945 4.02788 14.9333 3.73333 14.9333C3.43878 14.9333 3.2 14.6945 3.2 14.4V8.00001C3.2 7.70547 3.43878 7.46668 3.73333 7.46668Z'
                  fill='#1D1E20'
                />
              </svg>
            </div>
            <div className='text-accent-accent-12 text-base leading-6 font-bold tracking-normal'>
              Classifica Simulata
            </div>
          </div>
          <div id='spiegaclassifica' className='flex items-center justify-end'>
            {rankBoxDialog}
          </div>
        </div>
      </div>
      {nextChangeCopy()}
      <div
        id='rankinfo'
        className='flex w-full flex-col items-start gap-4 md:flex-row md:items-center md:justify-between'
      >
        <Skeleton loading={loading} className='h-18 w-24'>
          <div id='rankbox' className='flex items-center gap-4'>
            <div id='rank' className='flex items-start justify-between gap-2'>
              <div className='text-neutral-neutral-11 text-6xl leading-none font-medium tracking-tighter'>
                {playerInfo?.simulated_rank}
              </div>
            </div>
            {playerInfo?.first_rank_difference !== undefined && (
              <Badge id='rankvariation' className={rankVariationClassName}>
                {arrow}
                {changeRankText}
                rispetto a inizio anno
              </Badge>
            )}
          </div>
        </Skeleton>
        <Skeleton loading={loading} className='w-full'>
          <div id='slider' className='flex w-full flex-col items-start md:w-auto md:justify-end'>
            <div className='w-44 md:self-end'>
              <Slider value={[percentage]} size={'3'} variant='soft' className='rank-slider pb-3' />
            </div>
            <div className='text-neutral-neutral-12 flex w-full flex-col items-start text-xs font-medium tracking-wide md:text-right'>
              {percentage}% dei punti necessari a raggiungere la prossima soglia per la classifica di{' '}
              {getNextRank(playerInfo?.simulated_rank)}
              <div className='whitespace-nowrap'>
                <span className='text-lg font-bold'>
                  {playerInfo?.points_to_rank_switch && Math.abs(playerInfo.points_to_rank_switch)}
                </span>
                <span className='ml-1'>
                  {playerInfo?.points_to_rank_switch && rankSwitchLabel(playerInfo.points_to_rank_switch)}
                </span>
              </div>
            </div>
          </div>
        </Skeleton>
      </div>
    </div>
  );
};

export default RankBox;
